/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Grid, Typography } from '@mui/material';
import React from 'react';
import { SearchHighlight } from '@opengeoweb/shared';
import type { layerTypes, serviceTypes } from '@opengeoweb/store';
import type { LayerProps } from '@opengeoweb/webmap';
import { LayerInfoButton, getDimensionsList } from '@opengeoweb/webmap-react';
import type { DeleteLayerParams } from './LayerAddRemoveButton';
import { LayerAddRemoveButton } from './LayerAddRemoveButton';
import { LayerListDimensions } from './LayerListDimensions';
import { useLayerSelectTranslation } from '../../utils/i18n';
import {
  widthToColumns,
  widthToRowHeight,
} from '../LayerSelect/LayerSelectUtils';

interface AddLayerParams {
  serviceUrl: string;
  layerName: string;
}

interface LayerListRowProps {
  layer: LayerProps;
  service: serviceTypes.ReduxService;
  allLayers: layerTypes.ReduxLayer[];
  allMapIds: string[];
  addLayer: ({ serviceUrl, layerName }: AddLayerParams) => void;
  addLayerToAllMaps: ({ serviceUrl, layerName }: AddLayerParams) => void;
  deleteLayer: ({ layerId, layerIndex }: DeleteLayerParams) => void;
  removeLayerFromAllMaps: (layerId: string) => void;
  mapLayers: layerTypes.ReduxLayer[];
  layerSelectWidth: number;
  searchFilter: string;
  hasActiveLayer: boolean;
  onToggleActiveLayer: () => void;
}

const layerInfoStyle = {
  fontSize: '10px',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  lineHeight: 1,
};

export const LayerListRow: React.FC<LayerListRowProps> = ({
  layer,
  service,
  allLayers,
  allMapIds,
  addLayer,
  addLayerToAllMaps,
  deleteLayer,
  removeLayerFromAllMaps,
  mapLayers,
  layerSelectWidth,
  searchFilter,
  hasActiveLayer = false,
  onToggleActiveLayer,
}) => {
  const { t } = useLayerSelectTranslation();
  const rowHeight = widthToRowHeight(layerSelectWidth);
  const columns = widthToColumns(layerSelectWidth);
  const dimensions = getDimensionsList(layer.dimensions!);

  return (
    <Grid
      alignItems="center"
      container
      data-testid="layerListLayerRow"
      justifyContent="center"
      sx={{
        backgroundColor: 'geowebColors.cards.cardContainer',
        padding: '8px',
        marginBottom: '4px',
        height: rowHeight,
        border: 'solid 1px',
        borderColor: 'geowebColors.cards.cardContainerBorder',
      }}
    >
      <Grid container sx={columns.column8}>
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              fontSize: '12px',
              height: '14px',
              lineHeight: '14px',
              fontWeight: 500,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            <SearchHighlight search={searchFilter} text={layer.title!} />
          </Box>
        </Grid>
        <Grid container direction="column" sx={columns.column1}>
          <Box
            sx={{
              fontSize: '12px',
              height: '18px',
              lineHeight: '18px',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            <SearchHighlight search={searchFilter} text={service.name!} />
          </Box>
          <LayerInfoButton
            isActive={hasActiveLayer}
            onClick={onToggleActiveLayer}
          />
        </Grid>
        <Grid sx={columns.column2}>
          <Grid sx={columns.column3}>
            {!!layer.abstract && (
              <Typography data-testid="layerAbstract" sx={layerInfoStyle}>
                <SearchHighlight search={searchFilter} text={layer.abstract} />
              </Typography>
            )}
          </Grid>
          <Grid container sx={columns.column4}>
            <Grid sx={columns.column5}>
              <LayerListDimensions dimensions={dimensions} />
            </Grid>
            <Grid sx={columns.column6}>
              {layer.path.length > 0 && (
                <Typography sx={layerInfoStyle}>
                  {t('groups')}:{' '}
                  <SearchHighlight
                    search={searchFilter}
                    text={layer.path.join(' / ')}
                  />
                </Typography>
              )}
              {layer.keywords?.length! > 0 && (
                <Typography sx={layerInfoStyle}>
                  {t('keywords')}:{' '}
                  <SearchHighlight
                    search={searchFilter}
                    text={(layer.keywords ?? [])
                      .slice()
                      .sort((textA, textB) => {
                        return textA.localeCompare(textB);
                      })
                      .join(', ')}
                  />
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        alignItems="center"
        container
        justifyContent="flex-end"
        sx={columns.column9}
      >
        <Box
          alignItems="flex-start"
          sx={(columns.column7, { width: '100%', paddingLeft: '16px' })}
        >
          <LayerAddRemoveButton
            addLayer={addLayer}
            addLayerToAllMaps={addLayerToAllMaps}
            allLayers={allLayers}
            allMapIds={allMapIds}
            deleteLayer={deleteLayer}
            layer={layer as unknown as layerTypes.ReduxLayer}
            mapLayers={mapLayers}
            removeLayerFromAllMaps={removeLayerFromAllMaps}
            serviceUrl={service.serviceUrl!}
          />
        </Box>
      </Grid>
    </Grid>
  );
};
